import React, { useState, useCallback } from "react";

import {
  TRole,
  TEnhancedRole,
} from "@/api/role-management/role-management.types";
import { Typography } from "@/components/ui/Typography/Typography";
import { useTranslation } from "react-i18next";
import { RoleDetail } from "./components/RoleDetail";
import { RoleForm } from "./components/RoleForm";
import { RoleListView } from "./components/RoleListView";
import CommonCardUI from "@/components/ui/Card/CommonCardUI";
import { useNavigate } from "@tanstack/react-router";
import { usePermissions } from "@/hooks/usePermissions";
import { useBreadcrumb } from "@/hooks/useBreadcumb";
type TRoleViewMode = "list" | "add" | "edit" | "view";

const RoleManagement: React.FC = () => {
  const { t } = useTranslation(["roleManagementPage", "common"]);
  const [roleViewMode, setRoleViewMode] = useState<TRoleViewMode>("list");
  const [selectedRole, setSelectedRole] = useState<TRole>({} as TRole);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const getPageTitle = useCallback(() => {
    switch (roleViewMode) {
      case "add":
        return `${t("ROLES.CREATE_ROLE.TITLE")}`;
      case "edit":
        return `${t("ROLES.ROLE_CARD.EDIT_ROLE")}`;
      case "view":
        return `${t("ROLES.ROLE_CARD.VIEW_ROLE")}`;
      default:
        return t("TITLE");
    }
  }, [t, roleViewMode]);

  const breadcrumbRoutes = [
    {
      name: t("common:SIDEBAR_LABEL.CONFIGURATION"),
      path: "/configurations",
    },
    {
      name:
        getPageTitle() !== t("TITLE")
          ? `${t("TITLE")} - ${getPageTitle()}`
          : t("TITLE"),
      path: "/role-management",
    },
  ];
  useBreadcrumb(breadcrumbRoutes);

  // Role handlers
  const handleBackToRoleList = useCallback(() => {
    setRoleViewMode("list");
    setSelectedRole({} as TRole);
  }, []);

  const handleAddRole = useCallback(() => {
    if (!hasPermission("can-create-role-group")) return;
    setRoleViewMode("add");
    setSelectedRole({} as TRole);
  }, [hasPermission]);

  const handleEditRole = useCallback(
    (role: TRole | TEnhancedRole) => {
      if (!hasPermission("can-update-role-group")) return;
      setSelectedRole(role as TRole);
      setRoleViewMode("edit");
    },
    [hasPermission],
  );

  const handleViewRole = useCallback(
    (role: TRole | TEnhancedRole) => {
      if (!hasPermission("can-view-role-groups")) return;
      setSelectedRole(role as TRole);
      setRoleViewMode("view");
    },
    [hasPermission],
  );

  const handleRoleFormSave = useCallback(() => {
    setRoleViewMode("list");
    setSelectedRole({} as TRole);
  }, []);

  const handleAssignPermission = useCallback(
    (role: TRole) => {
      if (!hasPermission("can-view-permissions")) return;
      void navigate({
        to: "/role-management/assign-permission/$roleId",
        params: {
          roleId: role.internal_id, // use internal_id consistently
        },
      });
    },
    [hasPermission, navigate],
  );

  // New handlers for enhanced interface
  const handleRoleSelect = useCallback(
    (role: TRole | TEnhancedRole) => {
      handleAssignPermission(role as TRole);
    },
    [handleAssignPermission],
  );

  function renderRoleContent() {
    // Legacy interface for add/edit/view modes
    switch (roleViewMode) {
      case "add":
        return (
          <CommonCardUI
            classNameContent="p-0"
            className="custom-bx-shadow bg-background"
          >
            <section className="flex w-full flex-wrap items-center mb-3 pb-3 justify-between sm:flex-nowrap p-3 border-b border-dashed">
              <div>
                <Typography
                  affects={"semiBold"}
                  className="mt-0"
                  data-testid="labelTestId"
                >
                  {t("ROLES.CREATE_ROLE.TITLE")}
                </Typography>
                <Typography
                  variant="small"
                  affects="muted"
                  className="dark:text-muted-foreground"
                >
                  {t("ROLES.CREATE_ROLE.SUBTITLE")}
                </Typography>
              </div>
            </section>
            <div className="p-4">
              <RoleForm
                onSave={handleRoleFormSave}
                onCancel={handleBackToRoleList}
                isEdit={false}
              />
            </div>
          </CommonCardUI>
        );

      case "edit":
        return (
          <CommonCardUI
            classNameContent="p-0"
            className="custom-bx-shadow bg-background"
          >
            <section className="flex w-full flex-wrap items-center mb-3 pb-3 justify-between sm:flex-nowrap p-3 border-b border-dashed">
              <div>
                <Typography
                  variant="p"
                  affects={"semiBold"}
                  className="mt-0"
                  data-testid="labelTestId"
                >
                  {t("ROLES.EDIT_ROLE.TITLE")}
                </Typography>
                <Typography
                  variant="small"
                  affects="muted"
                  className="dark:text-muted-foreground"
                >
                  {t("ROLES.EDIT_ROLE.SUBTITLE", {
                    roleName: selectedRole?.name,
                  })}
                </Typography>
              </div>
            </section>
            <div className="p-4">
              <RoleForm
                role={selectedRole ?? undefined}
                onSave={handleRoleFormSave}
                onCancel={handleBackToRoleList}
                isEdit={true}
              />
            </div>
          </CommonCardUI>
        );

      case "view":
        return (
          <div className="space-y-6">
            {selectedRole && (
              <RoleDetail
                role={selectedRole}
                onRoleSelect={handleRoleSelect}
                onEdit={() => handleEditRole(selectedRole)}
                onBack={handleBackToRoleList}
              />
            )}
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <div className="space-y-3">
      {renderRoleContent()}
      {roleViewMode === "list" && (
        <div className="mt-3">
          <RoleListView
            onRoleSelect={handleRoleSelect}
            onAddRole={handleAddRole}
            onEditRole={handleEditRole}
            onViewRole={handleViewRole}
            selectedRole={selectedRole}
          />
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
